/** Roll a day-level insight series up to week or month without changing the amounts. */

function isoDate(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function parseIso(period) {
  const [year, month, day] = String(period || '').split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

export function presetDateRange(preset, now = new Date()) {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (preset === 'today') {
    const iso = isoDate(today);
    return { from_date: iso, to_date: iso };
  }
  if (preset === 'week') {
    const day = today.getDay();
    const mondayOffset = day === 0 ? -6 : 1 - day;
    const start = new Date(today);
    start.setDate(today.getDate() + mondayOffset);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return { from_date: isoDate(start), to_date: isoDate(end) };
  }
  if (preset === 'month') {
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return { from_date: isoDate(start), to_date: isoDate(end) };
  }
  const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const end = new Date(today.getFullYear(), today.getMonth(), 0);
  return { from_date: isoDate(start), to_date: isoDate(end) };
}

export function suggestedGrain(points) {
  const dates = (points || []).map((point) => parseIso(point.period)).filter(Boolean);
  if (dates.length < 2) return 'day';
  const span = Math.round((Math.max(...dates) - Math.min(...dates)) / 86400000);
  if (span <= 31) return 'day';
  if (span <= 120) return 'week';
  return 'month';
}

function bucketStart(period, grain) {
  const date = parseIso(period);
  if (!date) return period;
  if (grain === 'month') return isoDate(new Date(date.getFullYear(), date.getMonth(), 1));
  if (grain === 'week') {
    const day = date.getDay();
    const mondayOffset = day === 0 ? -6 : 1 - day;
    const start = new Date(date);
    start.setDate(date.getDate() + mondayOffset);
    return isoDate(start);
  }
  return isoDate(date);
}

export function rollUpInsightPoints(points, grain = 'day') {
  const grouped = new Map();
  (points || []).forEach((point) => {
    const period = bucketStart(point.period, grain);
    const series = point.series || '';
    const branch = point.branch || '';
    const key = `${period}|${series}|${branch}`;
    const current = grouped.get(key) || { period, series, branch };
    Object.entries(point).forEach(([field, value]) => {
      if (field === 'period' || field === 'series' || field === 'branch') return;
      const number = Number(value);
      if (!Number.isFinite(number)) return;
      current[field] = (Number(current[field]) || 0) + number;
    });
    if (!series) delete current.series;
    if (!branch) delete current.branch;
    grouped.set(key, current);
  });
  return Array.from(grouped.values()).sort((a, b) => String(a.period).localeCompare(String(b.period)));
}

export function sumPointsByPeriod(points) {
  const grouped = new Map();
  (points || []).forEach((point) => {
    const current = grouped.get(point.period) || { period: point.period };
    Object.entries(point).forEach(([field, value]) => {
      if (field === 'period' || field === 'series' || field === 'branch') return;
      const number = Number(value);
      if (!Number.isFinite(number)) return;
      current[field] = Math.round(((Number(current[field]) || 0) + number) * 100) / 100;
    });
    grouped.set(point.period, current);
  });
  return Array.from(grouped.values()).sort((a, b) => String(a.period).localeCompare(String(b.period)));
}

export function filtersFromSearch(searchParams, defaults = {}) {
  const arrayKeys = Object.entries(defaults).filter(([, value]) => Array.isArray(value)).map(([key]) => key);
  const result = { ...defaults };
  arrayKeys.forEach((key) => { result[key] = []; });
  let seen = false;
  searchParams.forEach((value, key) => {
    seen = true;
    if (arrayKeys.includes(key) || Array.isArray(result[key])) {
      if (!Array.isArray(result[key]) || (defaults[key] && !Array.isArray(defaults[key]))) result[key] = [];
      result[key].push(value);
      return;
    }
    if (key === 'include_child_branches') {
      result[key] = value !== 'false' && value !== '0';
      return;
    }
    result[key] = value;
  });
  if (!seen) return { ...defaults };
  return result;
}

export function filtersToSearch(filters = {}) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== '' && item != null) params.append(key, item);
      });
      return;
    }
    if (value !== '' && value != null) params.set(key, String(value));
  });
  return params.toString();
}

const PAYMENT_MODE_LABELS = {
  paid: 'Paid',
  to_pay: 'To Pay',
  topay: 'To Pay',
  credit: 'Credit',
  on_account: 'Credit',
  onaccount: 'Credit',
};

/** Show payment modes as To Pay, Paid, and Credit. Other labels stay as they are. */
export function displayPaymentLabel(value) {
  const key = String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  return PAYMENT_MODE_LABELS[key] || value;
}

export function pointsMatchFocus(point, focus) {
  if (!focus) return true;
  return point.series === focus || point.branch === focus;
}
